import React, { useState, useMemo } from "react"
import { array, bool, number, shape, string, oneOfType } from "prop-types"
import { Pane, StatusMessage } from "components/ui"
import { Box, Button, StackView } from "@planningcenter/tapestry-react"
import EventTimeContainer from "./EventTimeContainer"
import { formatDateShort, formatMonthDayCondensed, isInternational } from "utils/dates"
import { api } from "utils/api"
import moment from "moment-timezone"
import Icon from "components/planning_center_icon"
import { v4 as uuidv4 } from "uuid"

const SignUpDatesAndTimes = ({
  eventTimes: eventTimesFromProps,
  eventDatesAndTimesPath,
  mobileBrowser,
  momentDateTimeFormat,
  twentyFourHourTime,
  registerforSeparateTimesFeatureEnabled
}) => {
  const [status, setStatus] = useState(null)
  const [eventTimes, setEventTimes] = useState(eventTimesFromProps)
  const [isEditing, setIsEditing] = useState(false)
  const [showingAllEventTimes, setShowingAllEventTimes] = useState(false)

  const visibleEventTimes = useMemo(() => {
    return eventTimes.filter(eventTime => eventTime.deleteMe === "0")
  }, [eventTimes])

  const handleCancel = () => {
    setEventTimes(eventTimesFromProps)
    setIsEditing(false)
  }

  const addFirstEventTime = () => {
    setIsEditing(true)
    handleAddition()
  }

  const handleAddition = () => {
    const newKey = uuidv4()
    const previousEventTime = visibleEventTimes[visibleEventTimes.length - 1]

    const defaultEventTimeAttributes = {
      startsAt: moment()
        .add(1, "hour")
        .startOf("hour")
        .format(),
      endsAt: moment()
        .add(3, "hours")
        .startOf("hour")
        .format()
    }
    const newEventTime = {
      ...defaultEventTimeAttributes,
      ...previousEventTime,
      key: newKey,
      id: "",
      canBeDeleted: true,
      allDay: false
    }

    setEventTimes(eventTimes => [...eventTimes, newEventTime])
  }

  const handleChange = (key, payload) => {
    setEventTimes(currentTimes => {
      return currentTimes.map(eventTime => {
        if (eventTime.key === key) {
          eventTime = { ...eventTime, ...payload }
        }
        return eventTime
      })
    })
  }

  const handleSave = request => {
    setStatus({ text: "Saving...", icon: "spinner" })

    return request
      .then(response => {
        setStatus(null)
        setIsEditing(false)
        return response
      })
      .catch(response => {
        setStatus({
          text: response.errors || "Changes not saved",
          icon: "general#exclamation-triangle",
          type: "error"
        })

        setTimeout(() => setStatus(null), 3000)
      })
  }

  const handleEventTimeSave = () => {
    const event_times_attributes = eventTimes.map(et => ({
      id: et.id,
      starts_at: et.startsAt,
      ends_at: et.endsAt,
      all_day: et.allDay,
      _destroy: et.deleteMe
    }))

    handleSave(
      api.put(eventDatesAndTimesPath, {
        event: { event_times_attributes }
      })
    ).then(response => response && setEventTimes([...response.eventTimes]))
  }

  const eventTimesToShow = showingAllEventTimes ? eventTimes : eventTimes.slice(0, 5)

  return (
    <div className="pane">
      <div className="pane__header d-f ai-c jc-sb">
        <h2 className="pane__title">Dates and times</h2>
        <div className="d-f">
          {status && (
            <div className="mr-1">
              <StatusMessage {...status} />
            </div>
          )}
          {isEditing ? (
            <StackView axis="horizontal" spacing={1}>
              <Button title="Cancel" size="xs" variant="outline" onClick={handleCancel} />
              <Button title="Save" size="xs" theme="create" onClick={handleEventTimeSave} />
            </StackView>
          ) : (
            <Button
              title={eventTimes.length > 0 ? "Edit" : "Add date/time"}
              size="xs"
              variant="outline"
              onClick={eventTimes.length > 0 ? () => setIsEditing(true) : addFirstEventTime}
            />
          )}
        </div>
      </div>
      {isEditing ? (
        <Box padding={2}>
          <EventTimeContainer
            onChange={handleChange}
            handleAddEventTime={handleAddition}
            eventTimes={eventTimes}
            visibleEventTimes={visibleEventTimes}
            mobileBrowser={mobileBrowser}
            momentDateTimeFormat={momentDateTimeFormat}
            twentyFourHourTime={twentyFourHourTime}
            registerforSeparateTimesFeatureEnabled={registerforSeparateTimesFeatureEnabled}
          />
        </Box>
      ) : (
        <>
          {eventTimes.length > 0 ? (
            <div className="p-2">
              {eventTimesToShow.map(et => (
                <DateAndTime et={et} key={et.key} />
              ))}
              {eventTimes.length > 5 && (
                <div className="mt-1 ta-r">
                  <Button
                    title={showingAllEventTimes ? "View less" : "View all"}
                    size="sm"
                    variant="outline"
                    onClick={() => setShowingAllEventTimes(!showingAllEventTimes)}
                  />
                </div>
              )}
            </div>
          ) : (
            <Pane.BlankState margin={2}>
              <Box marginBottom={1}>
                If this signup is for an event on a specific date, you can add one or more dates and
                times. Dates and times are included on Church Center and in the confirmation email
                when people sign up.
              </Box>
            </Pane.BlankState>
          )}
        </>
      )}
    </div>
  )
}

const DateAndTime = ({ et }) => {
  const startsAt = moment(et.startsAt)
  const endsAt = moment(et.endsAt)
  const allDay = et.allDay
  const { twentyFourHourTime } = App?.currentOrganization || {}

  return (
    <div className="action-list__item p-1">
      {startsAt.isSame(endsAt, "day") ? (
        <div>
          {formatDateShort(startsAt)}
          {!allDay && (
            <div className="font-size-4">
              {twentyFourHourTime ? (
                <>
                  {startsAt.format("HH:mm")}
                  &ndash;
                  {endsAt.format("HH:mm")}
                </>
              ) : (
                <>
                  {startsAt.minute() === 0 ? startsAt.format("h") : startsAt.format("h:mm")}
                  {startsAt.format("a") !== endsAt.format("a") && startsAt.format("a")}
                  &ndash;
                  {endsAt.minute() === 0 ? endsAt.format("h") : endsAt.format("h:mm")}
                  {endsAt.format("a")}
                </>
              )}
            </div>
          )}
        </div>
      ) : (
        <>
          {allDay ? (
            <>
              {startsAt.isSame(endsAt, "year") ? (
                <>
                  {startsAt.isSame(endsAt, "month") ? (
                    <div className="d-f ai-c">
                      {/* 22-24 Jul, 2021 */}
                      {isInternational(startsAt) ? (
                        <>
                          {startsAt.format("D")}&ndash;
                          {endsAt.format("D")} {startsAt.format("MMM")}, {endsAt.format("YYYY")}
                        </>
                      ) : (
                        <>
                          {/* Jul 22-24, 2021 */}
                          {formatMonthDayCondensed(startsAt)}&ndash;
                          {endsAt.format("D, YYYY")}
                        </>
                      )}
                    </div>
                  ) : (
                    <>
                      {formatMonthDayCondensed(startsAt)} &ndash; {formatDateShort(endsAt)}
                    </>
                  )}
                </>
              ) : (
                <>
                  {formatDateShort(startsAt)} &ndash; {formatDateShort(endsAt)}
                </>
              )}
            </>
          ) : (
            <div className="d-f ai-c">
              <div css={{ width: "140px" }}>
                {formatDateShort(startsAt)}
                <div className="font-size-4">{startsAt.format(momentTimeFormat)}</div>
              </div>
              <div className="px-4">
                <Icon symbol="general#right-arrow" className="c-neutral-600" />
              </div>
              <div css={{ width: "140px" }}>
                {formatDateShort(endsAt)}
                <div className="font-size-4">{endsAt.format(momentTimeFormat)}</div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

DateAndTime.propTypes = {
  et: shape({
    id: oneOfType([string, number]),
    startsAt: string,
    endsAt: string,
    allDay: bool
  })
}

SignUpDatesAndTimes.propTypes = {
  eventTimes: array,
  editDatesAndTimesModalPath: string,
  eventDatesAndTimesPath: string,
  mobileBrowser: bool,
  momentDateTimeFormat: string,
  twentyFourHourTime: bool,
  registerforSeparateTimesFeatureEnabled: bool
}

export default SignUpDatesAndTimes
